import type {TFunction} from "i18next";
import {z} from "zod";

export type LoginFormValues = {
  user_name: string;
  password: string;
  remember_me: boolean;
  gps_coordinates?: string;
};

export type ForgotPasswordFormValues = {
  email: string;
};

export type ResetPasswordFormValues = {
  token: string;
  password: string;
  password_confirm: string;
};

export type ChangePasswordFormValues = {
  password: string;
  password_confirm: string;
};

export function createLoginSchema(t: TFunction<"auth">) {
  return z.object({
    user_name: z.string().min(1, {message: t("validation.username_required")}),
    password: z.string().min(1, {message: t("validation.password_required")}),
    remember_me: z.boolean().optional().default(false),
    gps_coordinates: z.string().optional(),
  });
}

export function createForgotPasswordSchema(t: TFunction<"auth">) {
  return z.object({
    email: z.string().email({message: t("validation.invalid_email")}),
  });
}

export function createResetPasswordSchema(t: TFunction<"auth">) {
  return z
    .object({
      token: z.string().min(1, {message: t("validation.token_required")}),
      password: z.string().min(1, {message: t("validation.password_required")}),
      password_confirm: z.string().min(1, {
        message: t("validation.password_confirm_required"),
      }),
    })
    .refine((data) => data.password === data.password_confirm, {
      message: t("validation.passwords_mismatch"),
      path: ["password_confirm"],
    });
}

export function createChangePasswordSchema(t: TFunction<"auth">) {
  return z
    .object({
      password: z.string().min(1, {message: t("validation.password_required")}),
      password_confirm: z.string().min(1, {
        message: t("validation.password_confirm_required"),
      }),
    })
    .refine((data) => data.password === data.password_confirm, {
      message: t("validation.passwords_mismatch"),
      path: ["password_confirm"],
    });
}

export const completePlaceholderProfileSchema = z.object({
  first_name: z
    .string()
    .trim()
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be at most 50 characters"),
  last_name: z
    .string()
    .trim()
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be at most 50 characters"),
  telephone: z.string().trim().max(20, "Phone must be at most 20 characters").optional(),
});
